import Configurator from '@m/config/Configurator';
import { loadStripe } from '@stripe/stripe-js/pure';

import type { Stripe, StripeError } from '@stripe/stripe-js';

let loadStripePromise: Promise<Stripe | null> | undefined;

function stripeLoader() {
  if (!loadStripePromise) {
    // TODO: Do we want to enable fraud signals?
    // They essentially track our webpage. May impact our ToS/PP
    // https://stripe.com/docs/disputes/prevention/advanced-fraud-detection
    loadStripe.setLoadParameters({ advancedFraudSignals: false });
    loadStripePromise = loadStripe(Configurator.STRIPE_PUBLISHABLE_KEY);
  }
  return loadStripePromise;
}

/**
 * We export `stripePromise` using the stripeLoader() function
 * which saves that value in a module scoped variable so that stripe
 * is only initialized once and won't break when hot-reloading
 */
export const stripePromise = stripeLoader();

export type StripeErrorWithPaymentIntent = StripeError & {
  payment_intent: NonNullable<StripeError['payment_intent']>;
};

/**
 * Checks if a Stripe error indicates that a PaymentIntent has already succeeded.
 * This can occur when a confirmation request is retried after a network failure,
 * and Stripe returns an error because the PaymentIntent was already confirmed.
 *
 * @param error - The Stripe error object from confirmPayment response
 * @returns true if the error indicates an already-succeeded payment
 */
export function isPaymentAlreadySucceeded(
  error: StripeError | undefined,
): error is StripeErrorWithPaymentIntent {
  if (!error) return false;

  return (
    error.type === 'invalid_request_error' &&
    error.code === 'payment_intent_unexpected_state' &&
    error.payment_intent?.status === 'succeeded'
  );
}
